import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import Logo from './components/Logo';
import DataChart from './components/DataChart';
import ContactModal from './components/ContactModal';
import VideoModal from './components/VideoModal';
import BenefitDetails from './pages/BenefitDetails';

const { FiTrendingUp, FiDollarSign, FiClock, FiTarget, FiUsers, FiBarChart3, FiShield, FiZap, FiArrowRight, FiPlay } = FiIcons;

function App() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState(null);

  const benefits = [
    {
      icon: FiDollarSign,
      title: "Lower Startup Costs",
      description: "Reduce initial investment by 50-70% compared to traditional restaurants. No dining room, less staff, smaller space.",
      stat: "70% savings"
    },
    {
      icon: FiClock,
      title: "Faster Time to Market",
      description: "Launch your food business in weeks, not months. Skip lengthy permit processes for dining areas.",
      stat: "30 days avg"
    },
    {
      icon: FiTarget,
      title: "Multiple Brand Strategy",
      description: "Operate 3-5 different food brands from one kitchen. Diversify revenue streams effortlessly.",
      stat: "5 brands/kitchen"
    },
    {
      icon: FiTrendingUp,
      title: "Higher Profit Margins",
      description: "Achieve 15-25% higher margins with reduced overhead, no servers, and optimized operations.",
      stat: "25% higher margins"
    },
    {
      icon: FiUsers,
      title: "Data-Driven Growth",
      description: "Access rich customer data from delivery platforms to optimize menus and marketing strategies.",
      stat: "Real-time analytics"
    },
    {
      icon: FiZap,
      title: "Rapid Scalability",
      description: "Scale to new locations quickly with proven kitchen models and streamlined operations.",
      stat: "3x faster growth"
    }
  ];

  const stats = [
    { number: "71%", label: "Growth in ghost kitchens since 2020" },
    { number: "$43B", label: "Projected market size by 2025" },
    { number: "50%", label: "Lower operational costs" },
    { number: "3x", label: "Faster expansion rate" }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      business: "Noodle Dynasty (5 locations)",
      quote: "We went from startup to $2M revenue in 18 months. Ghost kitchens made it possible.",
      revenue: "$2M ARR"
    },
    {
      name: "Marcus Rodriguez",
      business: "Taco Revolution (12 locations)",
      quote: "Lower overhead means higher profits. We're expanding to 20 cities this year.",
      revenue: "$5M ARR"
    },
    {
      name: "Lisa Park",
      business: "Healthy Bowls Co (8 locations)",
      quote: "The data insights alone increased our orders by 40%. Game changer.",
      revenue: "$3.2M ARR"
    }
  ];

  const handleGetStarted = () => {
    setIsContactModalOpen(true);
  };

  const handleWatchDemo = () => {
    setIsVideoModalOpen(true);
  };

  const handleBenefitClick = (benefitTitle) => {
    setSelectedBenefit(benefitTitle);
  };

  const handleBackToOverview = () => {
    setSelectedBenefit(null);
  };

  const handleLearnMore = () => {
    document.getElementById('benefits-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Show benefit detail page if a benefit is selected
  if (selectedBenefit) {
    return <BenefitDetails benefitType={selectedBenefit} onBack={handleBackToOverview} />;
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Navigation */}
        <nav className="relative z-10 px-4 sm:px-6 lg:px-8 pt-6">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Logo size="md" />
              <span className="text-white font-bold text-xl">Ghost Kitchen Food</span>
            </div>
            <button
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
                Ghost Kitchens Are
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent block">
                  The Future
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
                Revolutionize your food business with delivery-only kitchens. Lower costs, higher profits, unlimited growth potential.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <motion.button
                  onClick={handleGetStarted}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 flex items-center"
                >
                  Start Your Ghost Kitchen
                  <SafeIcon icon={FiArrowRight} className="ml-2" />
                </motion.button>
                <motion.button
                  onClick={handleWatchDemo}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-all duration-300 flex items-center"
                >
                  <SafeIcon icon={FiPlay} className="mr-2" />
                  Watch Demo
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-white/5 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl md:text-4xl font-bold text-purple-400 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-300 text-sm md:text-base">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Data Charts Section */}
        <DataChart />

        {/* Benefits Section */}
        <section id="benefits-section" className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Why Ghost Kitchens Win
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Every advantage you need to dominate the food delivery market
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:border-purple-400/50 transition-all duration-300 group cursor-pointer"
                  onClick={() => handleBenefitClick(benefit.title)}
                >
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-12 h-12 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <SafeIcon icon={benefit.icon} className="text-white text-xl" />
                  </div>
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-white">
                      {benefit.title}
                    </h3>
                    <span className="text-purple-400 text-sm font-semibold bg-purple-400/20 px-2 py-1 rounded">
                      {benefit.stat}
                    </span>
                  </div>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    {benefit.description}
                  </p>
                  <div className="flex items-center text-purple-300 text-sm font-semibold group-hover:text-purple-200 transition-colors">
                    <span>Learn more</span>
                    <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-gradient-to-r from-purple-900/30 to-pink-900/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Success Stories
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Real entrepreneurs, real results, real growth
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-white font-bold">{testimonial.name}</h4>
                      <p className="text-purple-300 text-sm">{testimonial.business}</p>
                    </div>
                    <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-sm font-bold">
                      {testimonial.revenue}
                    </span>
                  </div>
                  <blockquote className="text-gray-300 italic">
                    "{testimonial.quote}"
                  </blockquote>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Story Section */}
        <section className="py-20 bg-gradient-to-r from-purple-900/50 to-pink-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-white mb-6">
                  From Startup to $10M in 18 Months
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed mb-8">
                  CloudKitchens helped entrepreneurs launch multiple food brands with minimal investment. 
                  By focusing purely on delivery, they achieved profitability in month 3 and scaled to 
                  50+ locations across major cities.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center text-white">
                    <SafeIcon icon={FiBarChart3} className="text-green-400 mr-3" />
                    <span>300% revenue growth year-over-year</span>
                  </div>
                  <div className="flex items-center text-white">
                    <SafeIcon icon={FiShield} className="text-green-400 mr-3" />
                    <span>85% lower risk compared to traditional restaurants</span>
                  </div>
                  <div className="flex items-center text-white">
                    <SafeIcon icon={FiZap} className="text-green-400 mr-3" />
                    <span>Launch new brands in under 30 days</span>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Ready to Start?</h3>
                  <p className="text-purple-100 mb-6">
                    Join thousands of successful ghost kitchen operators who chose the smart path to food business success.
                  </p>
                  <button 
                    onClick={handleGetStarted}
                    className="w-full bg-white text-purple-600 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-300"
                  >
                    Get Started Today
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 bg-black/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <Logo size="sm" />
                <span className="text-white font-semibold">Ghost Kitchen Food</span>
              </div>
              <p className="text-gray-400 text-center">
                © 2024 Ghost Kitchen Food. The future of food delivery starts here.
              </p>
            </div>
          </div>
        </footer>
      </div>

      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
      
      <VideoModal 
        isOpen={isVideoModalOpen} 
        onClose={() => setIsVideoModalOpen(false)} 
      />
    </>
  );
}

export default App;